export type ErrorPageVariantKind = 'forbidden' | 'server' | 'generic';

export interface ErrorPageVariant {
  kind: ErrorPageVariantKind;
  /** bootstrap-icons class, e.g. "bi-shield-lock" — used only when `illustration` is unset. */
  icon: string;
  badgeIcon: string;
  /** When set, the card renders this decorative illustration instead of icon/badgeIcon. */
  illustration?: 'sww' | 'access';
  title: string;
  description: string;
  /** Label for the primary "retry" action. Omitted entirely (no button rendered) when there's nothing sensible to retry, e.g. 403. */
  retryLabel?: string;
  goHomeLabel: string;
  secondaryText: string;
  secondaryActionLabel: string;
}

const FORBIDDEN_VARIANT: ErrorPageVariant = {
  kind: 'forbidden',
  icon: 'bi-shield-lock',
  badgeIcon: 'bi-x-lg',
  illustration: 'access',
  title: 'Access Required',
  description: 'You do not currently have access to this content. Please contact support@aosphere.com for assistance.',
  goHomeLabel: 'Back to home',
  secondaryText: 'Arrived from an email link, or still stuck?',
  secondaryActionLabel: 'Contact support',
};

const SERVER_VARIANT: ErrorPageVariant = {
  kind: 'server',
  icon: 'bi-graph-up',
  badgeIcon: 'bi-exclamation-lg',
  illustration: 'sww',
  title: 'Something went wrong',
  description: 'We faced an unexpected issue. Please try again.',
  retryLabel: 'Try again',
  goHomeLabel: 'Go home',
  secondaryText: 'Arrived from an email link, or still stuck?',
  secondaryActionLabel: 'Contact support',
};

const GENERIC_VARIANT: ErrorPageVariant = {
  kind: 'generic',
  icon: 'bi-exclamation-triangle',
  badgeIcon: 'bi-arrow-clockwise',
  illustration: 'sww',
  title: 'Something went wrong',
  description: 'We faced an unexpected issue. Please try again.',
  retryLabel: 'Try again',
  goHomeLabel: 'Go home',
  secondaryText: 'Arrived from an email link, or still stuck?',
  secondaryActionLabel: 'Contact support',
};

/**
 * Single source of truth for "what does the error page look like". 403 and 500
 * get dedicated copy/iconography; every other status — 401 (the rare case that
 * reaches here instead of the session-expiry flow), 404, 408, 429, 502, 503,
 * network failures, and component/window errors that carry no status at all —
 * falls back to the generic variant.
 */
export function resolveErrorPageVariant(statusCode?: number): ErrorPageVariant {
  if (statusCode === 403) {
    return FORBIDDEN_VARIANT;
  }
  if (statusCode === 500) {
    return SERVER_VARIANT;
  }
  return GENERIC_VARIANT;
}
